import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useProfileEvents() {
  const logEvent = useCallback(
    async (eventType: string, payload: Record<string, any> = {}) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('profile_events').insert({
        user_id: user.id,
        event_type: eventType,
        payload,
      });
    },
    []
  );

  return {
    // Item events
    logItemCreated: useCallback(
      (itemType: string, boardId: string | null) =>
        logEvent('item_created', { item_type: itemType, board_id: boardId }),
      [logEvent]
    ),

    logItemMoved: useCallback(
      (itemId: string, fromBoardId: string | null, toBoardId: string | null) =>
        logEvent('item_moved', {
          item_id: itemId,
          from_board_id: fromBoardId,
          to_board_id: toBoardId,
        }),
      [logEvent]
    ),

    // Board events
    logBoardOpened: useCallback(
      (boardId: string) =>
        logEvent('board_opened', {
          board_id: boardId,
          opened_at: new Date().toISOString(),
        }),
      [logEvent]
    ),

    logBoardClosed: useCallback(
      (boardId: string, durationSeconds: number) =>
        logEvent('board_closed', {
          board_id: boardId,
          duration_seconds: durationSeconds,
        }),
      [logEvent]
    ),

    // Goal events
    logGoalPhaseChanged: useCallback(
      (goalId: string, oldPhase: string, newPhase: string) =>
        logEvent('goal_phase_changed', {
          goal_id: goalId,
          old_phase: oldPhase,
          new_phase: newPhase,
        }),
      [logEvent]
    ),

    logGoalCompleted: useCallback(
      (goalId: string) => logEvent('goal_completed', { goal_id: goalId }),
      [logEvent]
    ),

    // Partnr events
    logRecommendationAccepted: useCallback(
      (recommendationType: string, context: string) =>
        logEvent('partnr_recommendation_accepted', {
          type: recommendationType,
          context,
        }),
      [logEvent]
    ),

    logRecommendationDismissed: useCallback(
      (recommendationType: string, context: string) =>
        logEvent('partnr_recommendation_dismissed', {
          type: recommendationType,
          context,
        }),
      [logEvent]
    ),

    logNudgeActedOn: useCallback(
      (nudgeId: string) => logEvent('nudge_acted_on', { nudge_id: nudgeId }),
      [logEvent]
    ),

    logNudgeDismissed: useCallback(
      (nudgeId: string) => logEvent('nudge_dismissed', { nudge_id: nudgeId }),
      [logEvent]
    ),

    // Canvas session
    logCanvasSession: useCallback(
      (duration: number, itemsMoved: number, zoomRange: [number, number]) =>
        logEvent('canvas_session', {
          duration_seconds: duration,
          items_moved: itemsMoved,
          zoom_range: zoomRange,
        }),
      [logEvent]
    ),

    // Mind dump
    logMindDumpCompleted: useCallback(
      (type: string, goalsCount: number) =>
        logEvent('mind_dump_completed', { type, goals_count: goalsCount }),
      [logEvent]
    ),
  };
}
